import { useEffect, useState, useMemo } from 'react';
import { Github, Gitlab, FolderOpen } from 'lucide-react';
import TreeNode from './TreeNode';
import GithubConnector from './GithubConnector';
import GitLabConnector from './GitLabConnector';
import { useFile } from '../../context/FileContext';
import { buildFileTree } from '../../utils/treeBuilder';
import { processDroppedItems, processUploadedFiles } from '../../utils/fileSystem';
import GitHubClient from '../../api/GitHubClient';
import GitLabClient from '../../api/GitLabClient';
import '../../index.css';

const FileExplorer = () => {
  const {
    githubBranch,
    githubNameAndRepo,
    githubAccessToken,
    gitlabBranch,
    gitlabNameAndRepo,
    gitlabAccessToken,
    gitlabBaseUrl,
    source,
    setSource,
    localFiles,
    setLocalFiles,
    clearLocalFiles,
    focusedFilePath,
    setFocusedFilePath,
    selectedFilePath
  } = useFile();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.items) {
      const droppedFiles = await processDroppedItems(e.dataTransfer.items);
      setLocalFiles(prev => [...prev, ...droppedFiles]);
      setSource('local');
    }
  };

  // Fetch tree when GitHub repo/branch changes
  useEffect(() => {
    if (source !== 'github' || !githubNameAndRepo || !githubBranch) return;
    if (focusedFilePath) setSource('github');
    fetchGithubTree();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubNameAndRepo, githubBranch, githubAccessToken, focusedFilePath]);

  // Fetch tree when GitLab repo/branch changes
  useEffect(() => {
    if (source !== 'gitlab' || !gitlabNameAndRepo || !gitlabBranch) return;
    fetchGitlabTree();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitlabNameAndRepo, gitlabBranch, gitlabAccessToken]);

  const fetchGithubTree = async () => {
    try {
      const client = new GitHubClient(githubAccessToken);
      const fileTree = await client.getFileTree(githubNameAndRepo, githubBranch);
      setFiles(fileTree);
    } catch (error) {
      console.error('Error fetching GitHub file tree:', error);
    }
  };

  const fetchGitlabTree = async () => {
    try {
      const client = new GitLabClient(gitlabAccessToken, gitlabBaseUrl);
      const fileTree = await client.getFileTree(gitlabNameAndRepo, gitlabBranch);
      setFiles(fileTree);
    } catch (error) {
      console.error('Error fetching GitLab file tree:', error);
    }
  };

  const handleLocalUpload = (filesList) => {
    const filesArray = processUploadedFiles(filesList);
    setLocalFiles(filesArray);
    setSource('local');
  };

  const activeFiles = source === 'local' ? localFiles : files;
  const treeData = useMemo(() => buildFileTree(activeFiles), [activeFiles]);

  return (
    <div className="FileExplorer">
      <div className="app-brand">
        <a href="/" className="app-name">
          viewer.HelloTwinCAT.dev
        </a>
        <a href="https://hellotwincat.dev" className="app-subtitle" target="_blank" rel="noopener noreferrer">
          TwinCAT Tutorials
        </a>
      </div>

      <div className="source-selector">
        <button
          onClick={() => setSource('github')}
          className={source === 'github' ? 'active' : ''}
          title="GitHub"
        >
          <Github size={15} />
          GitHub
        </button>
        <button
          onClick={() => setSource('gitlab')}
          className={source === 'gitlab' ? 'active' : ''}
          title="GitLab"
        >
          <Gitlab size={15} />
          GitLab
        </button>
        <button
          onClick={() => setSource('local')}
          className={source === 'local' ? 'active' : ''}
          title="Local Folder"
        >
          <FolderOpen size={15} />
          Local
        </button>
      </div>

      {source === 'github' && <GithubConnector />}
      {source === 'gitlab' && <GitLabConnector />}
      {source === 'local' && (
        <>
          <h1>Upload Local Folder</h1>
          <p>
            When using drag and drop, folders are appended, so you can add multiple folders this
            way. Also drag and drop can add single files.
          </p>
          <div
            className={`local-uploader ${isDragging ? 'dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
          >
            <p>
              Drag & Drop <b>folder</b> here
            </p>
            <span>or select with dialog:</span>
            <input
              type="file"
              webkitdirectory="true"
              directory=""
              onChange={(e) => handleLocalUpload(e.target.files)}
            />
          </div>
          {localFiles.length > 0 && (
            <div className="clear-container">
              <button onClick={clearLocalFiles} className="clear-btn">
                Clear All
              </button>
            </div>
          )}
        </>
      )}

      <hr style={{ margin: '20px 0' }} />
      <h1>Files</h1>
      <div>
        {treeData.map(node => (
          <TreeNode
            key={node.name}
            node={node}
            focusedFilePath={focusedFilePath}
            selectedFilePath={selectedFilePath}
            onFocusCleared={() => setFocusedFilePath(null)}
          />
        ))}
      </div>
      <hr />
      <div>
        <h3>About</h3>
        <p>
          Supports .TcPOU, .TcGVL, .TcDUT, .TcTLEO, .TcIO files, reading out declarations and
          implementations, methods and properties.
        </p>
        <p>
          Also can read events from .tmc files. But may not load files if they are too long...
          lets say when you stuff all events into main tmc file.
        </p>
      </div>
    </div>
  );
};

export default FileExplorer;
